import replace from 'lodash/replace';
import fetchBuilder from 'fetch-retry';
import {
  FETCH_RETRY_DELAY_MS,
  MAX_RETRY_ATTEMPT,
} from 'src/constants/GlobalConstants';
import * as KatalMetrics from '@amzn/katal-metrics';
import { logger } from 'src/logger';
import { emitApiErrorMetrics, MetricsName } from 'src/metrics';

export interface APIResponse {
  body?: any;
  error?: any;
  status: string;
}

/**
 * Fetch with retries.
 * @param retries number of attempt
 * @param retryDelayInMs retry delay
 * @returns Promise<Response>
 */
const fetchWithRetry = (
  retries: number,
  retryDelayInMs: number,
  doNotRetryOn: number[] = [403],
): ((input: RequestInfo, init?: RequestInit) => Promise<Response>) => {
  return fetchBuilder(window.fetch, {
    retryDelay: function (attempt, error, response) {
      return Math.pow(2, attempt) * retryDelayInMs; //Exponential backoff
    },
    retryOn: function (attempt, error, response) {
      if (doNotRetryOn.includes(response.status)) {
        return false;
      }
      // retry on any network error, or 4xx or 5xx status codes
      if ((error !== null || response.status >= 400) && attempt < retries) {
        return true;
      }
      return false;
    },
  });
};

export const fetchRetry = fetchWithRetry(
  MAX_RETRY_ATTEMPT,
  FETCH_RETRY_DELAY_MS,
);

/**
 * This is generic fetch function to make API calls with retries and supports metrics and logging.
 * @param metrics the Katal Metrics Provider
 * @param input RequestInfo
 * @param init RequestInit
 * @returns Promise<APIResponse>
 */
export const fetchWithRetryAndCaptureMetrics = async (
  metricPublisher: KatalMetrics.Publisher,
  input: RequestInfo,
  init?: RequestInit,
): Promise<APIResponse> => {
  const metric = new KatalMetrics.Metric.HttpRequest(
    MetricsName.BACKEND_REQUEST,
  ).withMonitor();

  logger.info('API request for Input', { input, init });
  return fetchRetry(input, init)
    .then(async (response) => {
      const body = isHttpResponseJsonContent(response)
        ? await response.json()
        : await response.text();
      if (!response.ok) {
        emitApiErrorMetrics(response, metricPublisher);
        return Promise.reject({ error: body, status: response.status });
      }
      logger.info('API response is successful for Input', {
        input,
        init,
        body,
      });
      metric.setSuccess();
      metricPublisher.publish(metric);
      return Promise.resolve({ body, status: `${response.status}` });
    })
    .catch((error) => {
      logger.error(
        `API response is failure. Check context for details. ${JSON.stringify(
          error,
        )}`,
      );
      metric.setFailure();
      metricPublisher.publish(metric);
      const apiResponse: APIResponse = {
        error,
        status: error.status || 'FETCH_ERROR',
      };
      return Promise.reject(apiResponse);
    });
};

export const isHttpResponseJsonContent = (response: Response): boolean =>
  response?.headers?.get('Content-Type')?.includes('application/json')
    ? true
    : false;

export const replaceAllEspacedCharactersFromJson = (
  escapedJson: string,
): string => replace(escapedJson, /\\"/g, '"');

/**
 * Converts escaped JSON string to unescaped and transforms it to object.
 * @param escapedJson the json which is escaped.
 * @returns unescaped JSON object.
 */
export const processAndUnescapeCharactersToJson = (
  escapedJson: string,
): any => {
  if (!escapedJson) {
    return escapedJson;
  }
  try {
    const unescapedCharacters =
      replaceAllEspacedCharactersFromJson(escapedJson);
    return JSON.parse(unescapedCharacters);
  } catch (e) {
    logger.error('Error while parsing JSON string', { e });
    throw new Error(`Unable to parse JSON: ${escapedJson}`);
  }
};

/**
 * Parse JSON by handling error scenarios.
 * @param unescapedJson raw json.
 * @returns JSON representation of the string.
 */
export const parseJson = (unescapedJson: string): any => {
  if (!unescapedJson) {
    return unescapedJson;
  }
  try {
    return JSON.parse(unescapedJson);
  } catch (e) {
    logger.error('Error while parsing JSON string', { e });
    throw new Error(`Unable to parse JSON: ${unescapedJson}`);
  }
};

export const truncateText = (text: string, maxLength: number = 0): string => {
  if (!text) {
    return '';
  }
  if (maxLength <= 3) {
    return text.slice(0, maxLength).trim();
  }

  if (text.length <= maxLength - 3) {
    return text;
  }
  return `${text.slice(0, maxLength - 3).trim()}...`;
};
